import React, { useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@material-ui/core";
import { Field, Form, Formik, FormikHelpers } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";

import CustomPasswordField from "./CustomPasswordField";
import { useDynamicYupPasswordValidations } from "../../libs/yup-validations";
import { AppState } from "../../redux";
import { authService, usersService } from "../../api";
import routes from "../../routing/routes";
import { ChangePasswordBaseData } from "../../models/userData";

/**
 * Formik-managed values for the temporary password reset form.
 */
export interface TempPasswordFormValues {
  newPassword: string;
  confirmNewPassword: string;
}

/**
 * Props for the TempPasswordModal component which enforces a password change flow.
 */
interface TempPasswordModalProps {
  currentUserId: string;
  open: boolean;
  onSave: () => Promise<void> | void;
}

/**
 * Modal dialog that forces users with temporary passwords to update their credentials.
 */
const TempPasswordModal: React.FC<TempPasswordModalProps> = ({
  currentUserId,
  open,
  onSave,
}) => {
  const { t } = useTranslation(["authentication", "snackbars", "common"]);
  const securityPolicy = useSelector(
    (state: AppState) => state.securitySettings.securitySettings
  );

  const { YupValidationPassword, YupValidationConfirmPassword } =
    useDynamicYupPasswordValidations(securityPolicy);

  const validationSchema = useMemo(
    () =>
      yup.object({
        newPassword: YupValidationPassword,
        confirmNewPassword: YupValidationConfirmPassword("newPassword"),
      }),
    [YupValidationPassword, YupValidationConfirmPassword]
  );

  const initialValues: TempPasswordFormValues = useMemo(
    () => ({
      newPassword: "",
      confirmNewPassword: "",
    }),
    []
  );

  /**
   * Submits the new password to the API, invokes the parent save handler, and surfaces feedback to the user.
   */
  const handleSave = useCallback(
    async (
      values: TempPasswordFormValues,
      helpers: FormikHelpers<TempPasswordFormValues>
    ) => {
      try {
        const payload: ChangePasswordBaseData = {
          new_password: values.newPassword,
          new_password_confirm: values.confirmNewPassword,
        };

        const response = await usersService.changeTemporaryPassword(
          currentUserId,
          payload
        );

        if (!response) {
          enqueueSnackbar(t("profile.passwordChangeFailure", { ns: "snackbars" }), {
            variant: "success",
          });
          return;
        }

        await Promise.resolve(onSave());

        enqueueSnackbar(t("profile.passwordChanged", { ns: "snackbars" }), {
          variant: "success",
        });
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [currentUserId, onSave, t]
  );

  /**
   * Logs the user out and returns them to the login page if they decline the password update.
   */
  const handleDismiss = useCallback(() => {
    authService.logout();
    window.location.href = routes.login;
  }, []);

  return (
    <Dialog open={open}>
      <DialogTitle>
        {t("password.tempPasswordTitle", {
          ns: "authentication",
        })}
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            {t("password.tempPasswordDescription", {
              ns: "authentication",
            })}
          </Typography>
        </Box>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ isValid, isSubmitting }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={CustomPasswordField}
                    fullWidth
                    autoComplete="new-password"
                    id="new-password"
                    label={t("password.change.passwordNew", {
                      ns: "authentication",
                    })}
                    name="newPassword"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={CustomPasswordField}
                    fullWidth
                    autoComplete="new-password"
                    id="confirm-new-password"
                    label={t("password.change.passwordNewConfirm", {
                      ns: "authentication",
                    })}
                    name="confirmNewPassword"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    size="large"
                    variant="contained"
                    color="primary"
                    disableElevation
                    disableRipple
                    disabled={!isValid || isSubmitting}
                  >
                    {t("save", { ns: "common" })}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} color="secondary">
          {t("decline", { ns: "common" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TempPasswordModal;
